import { Layout, theme } from "antd";
import { Activity, Suspense, lazy, useEffect } from "react";
import { useLocation, useRoutes } from "react-router-dom";

import { FooterContent } from "./components/FooterContent";
import { HeaderContent } from "./components/HeaderContent";
import { NoData } from "./components/NoData";
import { RemoveTrailingSlash } from "./components/RemoveTrailingSlash";
import { SpinnerPage } from "./components/SpinnerPage";
import type { LocationState } from "./interfaces";
import ConfigProvider from "./providers/ConfigProvider";
import { appRoutes, useAppRoutes } from "./routes/appRoutes";
import { syncChatwootCurrentPage } from "./utils/chatwootService";

const { Header, Content, Footer } = Layout;

const ServicePageOverlay = lazy(
  () => import("./pages/Service/ServicePageOverlay"),
);

export default function View() {
  const location = useLocation();
  const { token } = theme.useToken();

  useEffect(() => {
    syncChatwootCurrentPage();
  }, [location.pathname, location.search]);

  const state = location.state as LocationState | null;
  const hasOverlay =
    Boolean(state?.backgroundLocation) &&
    location.pathname.startsWith(`${appRoutes.servicePage}/`);

  const routesConfig = useAppRoutes();

  const baseRoutes = useRoutes(
    routesConfig,
    state?.backgroundLocation || location,
  );

  const overlayRoutes = useRoutes(
    [
      {
        path: `${appRoutes.servicePage}/:id`,
        element: <ServicePageOverlay />,
      },
      {
        path: "*",
        element: null,
      },
    ],
    location,
  );

  return (
    <ConfigProvider
      renderEmpty={() => (
        <NoData
          main={(state?.backgroundLocation || location).pathname === "/"}
        />
      )}
    >
      <Layout
        style={{
          position: "absolute",
          width: "100%",
        }}
      >
        <Header
          style={{
            position: "fixed",
            left: 0,
            right: 0,
            zIndex: 100,
            display: "flex",
            alignItems: "center",
            gap: "50px",
            backgroundRepeat: "no-repeat",
            background: `linear-gradient(to bottom, color-mix(in srgb, ${token.colorPrimary} 10%, ${token.colorWhite}) 98%, color-mix(in srgb, ${token.colorPrimary} 15%, ${token.colorWhite}) 2%)`,
          }}
        >
          <HeaderContent />
        </Header>
        <Content
          style={{
            marginBlockStart: "52px",
            marginBlockEnd: "22px",
            minHeight: "calc(100vh - 52px - 22px)",
          }}
        >
          <RemoveTrailingSlash />
          <Suspense fallback={<SpinnerPage />}>
            <Activity mode={hasOverlay ? "hidden" : "visible"}>
              {baseRoutes}
            </Activity>

            {hasOverlay ? overlayRoutes : null}
          </Suspense>
        </Content>
        <Footer
          style={{
            color: "rgba(255, 255, 255, 0.4)",
            zIndex: "1000",
            position: "fixed",
            bottom: 0,
            width: "100%",
            padding: "4px",
          }}
        >
          <FooterContent />
        </Footer>
      </Layout>
    </ConfigProvider>
  );
}
